import axios from 'axios';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';
import { AdminAccessFrame } from '../components/AdminAccessFrame';
import { useAuth } from '@edusense/shared-auth-core';
import { beginAdminMfaSetup, getAdminMfaStatus, verifyAdminMfa } from '../auth/adminMfaApi';
import { resolvePostAuthDestination } from '../auth/adminMfaRouting';
import { continueAdminPortalLogin } from '../auth/continueAdminPortalLogin';
import {
  normalizeAdminMfaVerifyResponse,
  resolveActiveAdminMfaChallengeId,
  shouldOfferAdminLoginRestart,
  type AdminMfaSetupResponse,
  type AdminMfaStatus,
} from '../types/adminMfa';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';

type PageState = {
  loading: boolean;
  submitting: boolean;
  error: string | null;
};

const getFailureReason = (error: unknown): string | null => {
  if (axios.isAxiosError(error) && error.response?.data) {
    return normalizeAdminMfaVerifyResponse(error.response.data).failureReason;
  }

  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const failureReason = record['failureReason'];
    if (typeof failureReason === 'string' && failureReason.trim()) {
      return failureReason;
    }
  }

  return null;
};

const getErrorMessage = (error: unknown, needsSetup: boolean): string => {
  const failureReason = getFailureReason(error);
  if (failureReason === 'expired_challenge') {
    return needsSetup
      ? '설정 세션이 만료되었습니다. 같은 시크릿으로 새 challenge를 발급했습니다. 인증 앱의 현재 6자리 코드를 다시 입력하세요.'
      : 'MFA 세션이 만료되었습니다. 관리자 로그인을 다시 시작하세요.';
  }
  if (failureReason === 'invalid_code') {
    return '인증 앱의 현재 6자리 코드를 다시 입력하세요.';
  }
  if (failureReason === 'invalid_challenge') {
    return needsSetup
      ? '설정 세션을 찾지 못했습니다. MFA 설정 시작을 다시 눌러 주세요.'
      : 'MFA 세션을 찾지 못했습니다. 관리자 로그인을 다시 시작하세요.';
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const message = record['message'] || record['error'] || record['detail'];
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return 'MFA 요청을 처리하지 못했습니다.';
};

const resolveDestination = (location: ReturnType<typeof useLocation>): string => {
  return resolvePostAuthDestination({
    nextParam: new URLSearchParams(location.search).get('next'),
    locationStateFrom: (location.state as { from?: string } | null)?.from ?? null,
    storedRedirect: localStorage.getItem('oauth2_post_login_redirect'),
    storedState: localStorage.getItem('oauth2_state'),
    fallbackPath: '/',
  });
};

const resolveChallengeId = (location: ReturnType<typeof useLocation>, setup: AdminMfaSetupResponse | null): string => {
  if (setup?.challengeId) {
    return setup.challengeId;
  }

  const params = new URLSearchParams(location.search);
  return params.get('challengeId') || params.get('mfa_challenge_id') || '';
};

export default function AdminMfaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const [status, setStatus] = useState<AdminMfaStatus | null>(null);
  const [setup, setSetup] = useState<AdminMfaSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [state, setState] = useState<PageState>({
    loading: true,
    submitting: false,
    error: null,
  });

  const destination = useMemo(() => resolveDestination(location), [location]);
  const queryChallengeId = useMemo(() => resolveChallengeId(location, null), [location]);
  const needsSetup = Boolean(status && (!status.mfaEnabled || status.setupRequired));
  const activeChallengeId = resolveActiveAdminMfaChallengeId({
    setupChallengeId: setup?.challengeId,
    queryChallengeId,
    statusChallengeId: status?.challengeId,
  });
  const shouldShowLoginRestart = shouldOfferAdminLoginRestart(state.error);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    const controller = new AbortController();

    const loadStatus = async () => {
      setState({ loading: true, submitting: false, error: null });
      try {
        const nextStatus = await getAdminMfaStatus(controller.signal);
        setStatus(nextStatus);

        if (nextStatus.sessionMfaVerified) {
          if (isAuthenticated) {
            navigate(destination, { replace: true });
          } else {
            await continueAdminPortalLogin(destination);
          }
          return;
        }

        setState({ loading: false, submitting: false, error: null });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        setState({ loading: false, submitting: false, error: getErrorMessage(error, false) });
      }
    };

    void loadStatus();
    return () => controller.abort();
  }, [authLoading, destination, isAuthenticated, navigate]);

  const handleSetup = async () => {
    setState((current) => ({ ...current, submitting: true, error: null }));

    try {
      const response = await beginAdminMfaSetup();
      setSetup(response);
      setCode('');
      setState({ loading: false, submitting: false, error: null });
    } catch (error) {
      setState({ loading: false, submitting: false, error: getErrorMessage(error, true) });
    }
  };

  const handleVerify = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!activeChallengeId || !code.trim()) {
      setState((current) => ({
        ...current,
        error: '인증 코드를 입력해주세요.',
      }));
      return;
    }

    setState((current) => ({ ...current, submitting: true, error: null }));

    try {
      await verifyAdminMfa({
        challengeId: activeChallengeId,
        code: code.trim(),
      });

      const nextStatus = await getAdminMfaStatus();
      setStatus(nextStatus);

      if (nextStatus.sessionMfaVerified) {
        if (isAuthenticated) {
          navigate(destination, { replace: true });
        } else {
          await continueAdminPortalLogin(destination);
        }
        return;
      }

      setState({
        loading: false,
        submitting: false,
        error: '인증은 처리됐지만 세션 상태를 확인하지 못했습니다. 다시 시도해주세요.',
      });
    } catch (error) {
      const failureReason = getFailureReason(error);

      if (needsSetup && failureReason === 'expired_challenge') {
        try {
          const refreshedSetup = await beginAdminMfaSetup();
          setSetup(refreshedSetup);
          setCode('');
        } catch {
          // Keep the original expiry error if setup refresh fails.
        }
      }

      setState({ loading: false, submitting: false, error: getErrorMessage(error, needsSetup) });
    }
  };

  if (authLoading || state.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="flex flex-col items-center gap-4 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
          <div className="space-y-1">
            <p className="font-medium text-slate-900">관리자 MFA 상태를 확인하는 중입니다.</p>
            <p className="text-sm text-slate-500">세션 검증이 끝나면 계속 진행합니다.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminAccessFrame step={2}>
      <Card className="w-full border-0 bg-transparent shadow-none">
        <CardHeader className="space-y-5 p-0 pb-6">
          <div className="flex items-center gap-3">
            {needsSetup ? (
              <ShieldAlert className="h-6 w-6 shrink-0 text-amber-600" />
            ) : (
              <ShieldCheck className="h-6 w-6 shrink-0 text-primary" />
            )}
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">관리자 인증</h1>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">관리자 세션을 확인합니다.</p>
              {status?.username ? <p className="mt-1 break-all text-xs text-muted-foreground">{status.username}</p> : null}
            </div>
          </div>

          {state.error && (
            <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              <p>{state.error}</p>
              {shouldShowLoginRestart && (
                <div className="mt-3">
                  <Button type="button" variant="outline" onClick={() => void logout()}>
                    로그인 다시 시작
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-6 p-0">
          <div className="rounded-lg border bg-muted/50 p-4 text-sm leading-6 text-muted-foreground">
            {needsSetup ? (
              <p>
                이 계정은 아직 TOTP MFA 설정이 완료되지 않았습니다. 시크릿을 발급한 뒤 인증 앱에 등록하고,
                생성된 6자리 코드를 입력해 세션을 검증하세요.
              </p>
            ) : (
              <p>
                인증 앱에서 현재 TOTP 코드를 확인한 뒤 입력하세요. 검증이 끝나면 원래 보려던 관리자 페이지로
                이동합니다.
              </p>
            )}
          </div>

          {needsSetup && !setup && (
            <div className="space-y-3">
              <Button type="button" onClick={() => void handleSetup()} disabled={state.submitting}>
                {state.submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    시크릿 생성 중...
                  </>
                ) : (
                  'MFA 설정 시작'
                )}
              </Button>
            </div>
          )}

          {setup && (
            <div className="space-y-4 rounded-lg border p-4">
              <div className="space-y-1">
                <p className="text-sm font-medium">Manual setup secret</p>
                <p className="break-all rounded bg-slate-100 px-3 py-2 font-mono text-sm">{setup.secret}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">OTP Auth URI</p>
                <p className="break-all rounded bg-slate-100 px-3 py-2 font-mono text-xs text-slate-600">
                  {setup.otpauthUri}
                </p>
              </div>
            </div>
          )}

          {!needsSetup && !activeChallengeId && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p>진행 중인 MFA challenge 정보를 찾지 못했습니다. 관리자 로그인을 다시 시작한 뒤 이 화면으로 돌아오세요.</p>
              <div className="mt-3">
                <Button type="button" variant="outline" onClick={() => void logout()}>
                  로그인 다시 시작
                </Button>
              </div>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleVerify}>
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="mfa-code">
                인증 코드
              </label>
              <Input
                id="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                className="h-14 px-4 font-mono text-xl tracking-[0.4em]"
                aria-describedby="mfa-code-help"
                value={code}
                maxLength={6}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <p id="mfa-code-help" className="pt-1 text-xs leading-5 text-muted-foreground">인증 앱에 표시된 6자리 숫자를 입력하세요.</p>
            </div>

            <div className="flex flex-col gap-3">
              <Button type="submit" className="h-12 w-full" disabled={state.submitting || !activeChallengeId || code.length !== 6}>
                {state.submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    검증 중...
                  </>
                ) : (
                  '인증하고 계속하기'
                )}
              </Button>

              {needsSetup && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleSetup()}
                  disabled={state.submitting}
                >
                  시크릿 다시 생성
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </AdminAccessFrame>
  );
}
